/* eslint-disable no-magic-numbers */
/**
 * Worker-safe quantized mesh geometry construction (no Three.js). Used by both
 * the geometry workers and the main-thread fallback.
 */

import type {
  BuildGeometryBuffersParams,
  ConstructedGeometryBuffers,
} from './construct-geometry.types.js';

export function computeVertexNormals(
  positions: Float32Array,
  indices: Uint16Array | Uint32Array,
  vertexCount: number
): Float32Array {
  const normals = new Float32Array(vertexCount * 3);

  const vA = [0, 0, 0];
  const vB = [0, 0, 0];
  const vC = [0, 0, 0];
  const cb = [0, 0, 0];
  const ab = [0, 0, 0];

  for (let i = 0; i < indices.length; i += 3) {
    const ia = indices[i];
    const ib = indices[i + 1];
    const ic = indices[i + 2];

    vA[0] = positions[ia * 3];
    vA[1] = positions[ia * 3 + 1];
    vA[2] = positions[ia * 3 + 2];

    vB[0] = positions[ib * 3];
    vB[1] = positions[ib * 3 + 1];
    vB[2] = positions[ib * 3 + 2];

    vC[0] = positions[ic * 3];
    vC[1] = positions[ic * 3 + 1];
    vC[2] = positions[ic * 3 + 2];

    cb[0] = vC[0] - vB[0];
    cb[1] = vC[1] - vB[1];
    cb[2] = vC[2] - vB[2];

    ab[0] = vA[0] - vB[0];
    ab[1] = vA[1] - vB[1];
    ab[2] = vA[2] - vB[2];

    const nx = cb[1] * ab[2] - cb[2] * ab[1];
    const ny = cb[2] * ab[0] - cb[0] * ab[2];
    const nz = cb[0] * ab[1] - cb[1] * ab[0];

    normals[ia * 3] += nx;
    normals[ia * 3 + 1] += ny;
    normals[ia * 3 + 2] += nz;

    normals[ib * 3] += nx;
    normals[ib * 3 + 1] += ny;
    normals[ib * 3 + 2] += nz;

    normals[ic * 3] += nx;
    normals[ic * 3 + 1] += ny;
    normals[ic * 3 + 2] += nz;
  }

  for (let i = 0; i < vertexCount; i++) {
    const idx = i * 3;
    const x = normals[idx];
    const y = normals[idx + 1];
    const z = normals[idx + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len > 0) {
      normals[idx] = x / len;
      normals[idx + 1] = y / len;
      normals[idx + 2] = z / len;
    }
  }

  return normals;
}

export function buildGeometryBuffers(
  params: BuildGeometryBuffersParams
): ConstructedGeometryBuffers {
  const {
    vertexData,
    triangleIndices,
    header,
    mapBox,
    mapToScene,
    baseHeight,
    initialBaseHeight,
  } = params;

  const elementsPerVertex = 3;
  const vertexCount = vertexData.length / elementsPerVertex;
  const positions = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);

  const mapRangeX = mapBox.max.x - mapBox.min.x;
  const mapRangeY = mapBox.max.y - mapBox.min.y;
  const heightRange = header.maxHeight - header.minHeight;

  const sceneOrigin = { x: 0, y: 0 };
  mapToScene(mapBox.min.x, mapBox.min.y, header.minHeight, sceneOrigin);

  const localOrigin = {
    x: sceneOrigin.x,
    y: sceneOrigin.y,
    z: 0,
  };

  const scenePosition = { x: 0, y: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (let i = 0; i < vertexCount; i++) {
    const u = vertexData[i];
    const v = vertexData[i + vertexCount];
    const h = vertexData[i + vertexCount * 2];

    const mapX = mapBox.min.x + (u / 32767.0) * mapRangeX;
    const mapY = mapBox.min.y + (v / 32767.0) * mapRangeY;
    let mapZ = header.minHeight + (h / 32767.0) * heightRange;

    if (mapZ === initialBaseHeight) {
      mapZ = baseHeight;
    }

    mapToScene(mapX, mapY, mapZ, scenePosition);

    const relX = scenePosition.x - localOrigin.x;
    const relY = scenePosition.y - localOrigin.y;

    const bufferIndex = i * 3;
    positions[bufferIndex] = relX;
    positions[bufferIndex + 1] = relY;
    positions[bufferIndex + 2] = mapZ;

    minX = Math.min(minX, relX);
    minY = Math.min(minY, relY);
    minZ = Math.min(minZ, mapZ);
    maxX = Math.max(maxX, relX);
    maxY = Math.max(maxY, relY);
    maxZ = Math.max(maxZ, mapZ);

    uvs[i * 2] = u / 32767.0;
    uvs[i * 2 + 1] = v / 32767.0;
  }

  const normals = computeVertexNormals(positions, triangleIndices, vertexCount);

  return {
    positions,
    uvs,
    normals,
    triangleIndices,
    localOrigin,
    boundingBox: {
      min: { x: minX, y: minY, z: minZ },
      max: { x: maxX, y: maxY, z: maxZ },
    },
  };
}
